import {produce} from 'immer';
import {throttle} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import toast from '@/components/HotToast';
import {useEscpacePress} from '@/hooks';
import {AnalysisItem} from '@/map/@types';
import {MESSAGES, SHAPE_COLOR_OPTIONS} from '@/map/constants';
import {AreaOverlayType} from '@/map/enums';
import {getKm2} from '@/map/utils';
import {getIntlString} from '@/utils';
import {DrawInfoCursor, DrawStartCursor} from '../MapCursor';
import {PolygonArea} from '../Overlay';

interface IProps {
  map: naver.maps.Map;
  item: AnalysisItem['polygon'];
  isActiveZoomLevel?: boolean;
  onComplete?(item: AnalysisItem['polygon']): void;
  onCancel?(): void;
}

type Coord = naver.maps.Coord;

const MIN_POLYGON_POINT = 2;
const navermaps = naver?.maps;

const colors = SHAPE_COLOR_OPTIONS[AreaOverlayType.POLYGON];

export const PolygonDrawingLayer = ({
  map,
  item,
  isActiveZoomLevel,
  onComplete,
  onCancel,
}: IProps) => {
  const evt = useRef<{[name: string]: naver.maps.MapEventListener}>({});

  const [paths, setPaths] = useState<Coord[]>([]);
  const [onFinish, setOnFinish] = useState<boolean>(false);
  const [clickPoint, setClickPoint] = useState<Coord | undefined>(item?.point);
  const [guidePoint, setGuidePoint] = useState<Coord>();

  const [instance, setInstance] = useState<naver.maps.Polygon>();

  const m2 = instance ? instance.getAreaSize() : 0;

  const polygonPaths = useMemo(() => {
    if (!paths.length) {
      return;
    }

    if (guidePoint && !guidePoint.equals(paths[paths.length - 1])) {
      return [[...paths, guidePoint]];
    }

    return [paths];
  }, [guidePoint, paths]);

  const pathSize = useMemo(() => {
    return polygonPaths?.[0]?.length ?? 0;
  }, [polygonPaths]);

  const clearEvent = () => {
    if (evt?.current) {
      Object.values(evt.current).map((event) =>
        naver?.maps.Event.removeListener(event)
      );
      evt.current = {};
    }
  };

  const onEscapePress = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  useEscpacePress(onEscapePress);

  useEffect(() => {
    if (!isActiveZoomLevel) {
      toast(
        getIntlString(MESSAGES, 'area-analytics-zoom-warning', {
          type: '다각형 분석을',
        })
      );
    }
  }, [isActiveZoomLevel]);

  const updatePath = useCallback((coord: Coord) => {
    setPaths(
      produce((prev) => {
        prev.push(coord);
      })
    );
    setClickPoint(undefined);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMouseDownEvent = useCallback(
    throttle((e: {type: string; originalEvent: MouseEvent; coord: Coord}) => {
      if (!map || e?.originalEvent?.button === 1) {
        return;
      }

      const isRightClick = e?.originalEvent?.button === 2;
      const nextCenter = map.getCenter();

      if (isRightClick) {
        setClickPoint(e.coord);
      } else {
        const prevCenter = map.getCenter();

        setTimeout(() => {
          if (prevCenter.equals(nextCenter)) {
            setClickPoint(e.coord);
          }
        }, 100);
      }
    }, 500),
    [map, setClickPoint]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMousemoveEvent = useCallback(
    throttle((e: {coord: Coord}) => {
      setGuidePoint(e.coord);
    }, 50),
    []
  );

  const onKeypress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Backspace') {
      setPaths(
        produce((prev) => {
          prev.pop();
        })
      );
    }
  }, []);

  useEffect(() => {
    if (onKeypress) {
      document.addEventListener('keydown', onKeypress);
    } else {
      document.removeEventListener('keydown', onKeypress);
    }

    return () => {
      document.removeEventListener('keydown', onKeypress);
    };
  }, [onKeypress]);

  useEffect(() => {
    if (onFinish) {
      if (!isActiveZoomLevel) {
        setOnFinish(false);
        return;
      }

      setGuidePoint(undefined);
      setClickPoint(undefined);

      if (paths.length > MIN_POLYGON_POINT) {
        onComplete?.({
          m2,
          paths: [[...paths, paths[0]]],
        });
      }

      setOnFinish(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFinish]);

  useEffect(() => {
    if (map) {
      if (evt.current.mousedown) {
        navermaps.Event.removeListener(evt.current.mousedown);
        delete evt.current.mousedown;
      }
      if (evt.current.rightclick) {
        navermaps.Event.removeListener(evt.current.rightclick);
        delete evt.current.rightclick;
      }

      evt.current.mousedown = navermaps.Event.addListener(
        map,
        'mousedown',
        onMouseDownEvent
      );

      evt.current.rightclick = navermaps.Event.addListener(
        map,
        'rightclick',
        () => setOnFinish(true)
      );
    } else {
      clearEvent();
      setOnFinish(true);
    }

    return () => {
      clearEvent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (clickPoint && isActiveZoomLevel) {
      updatePath(clickPoint);

      if (!evt.current.mousemove) {
        evt.current.mousemove = navermaps.Event.addListener(
          map,
          'mousemove',
          onMousemoveEvent
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickPoint]);

  useEffect(() => {
    if (item && item.paths && item.paths.length) {
      const last = item.paths.pop()?.pop();

      if (last) {
        setClickPoint(last);
      }
    }
  }, [item]);

  if (!map) {
    return null;
  }

  return (
    <>
      {!pathSize && (
        <DrawStartCursor map={map} hint="분석" color={colors.strokeColor} />
      )}
      {!!guidePoint && pathSize > 1 && (
        <DrawInfoCursor
          map={map}
          color={colors.strokeColor}
          position={guidePoint}
          hint="분석"
          typeText={m2 > 0 ? '면적' : ''}
          valueText={m2 > 0 ? `${getKm2(m2)}k㎡` : ''}
          menu={
            paths && paths.length >= MIN_POLYGON_POINT
              ? {'점 삭제': 'Backspace', 선택: '우클릭', 취소: 'Esc'}
              : {'점 삭제': 'Backspace', 취소: 'Esc'}
          }
          onEscKeyPress={onEscapePress}
        />
      )}
      {!!polygonPaths?.length && (
        <PolygonArea
          map={map}
          paths={polygonPaths}
          colorOptions={colors}
          dotOptions={{size: 6, filledEndDot: true}}
          polygonOptions={{
            strokeWeight: 2,
          }}
          onLoaded={(instance) => setInstance(instance)}
        />
      )}
    </>
  );
};
